import React, { useState, useEffect, useRef } from 'react';
import { LuckyWheel } from 'react-luck-draw';
import './LotteryForm.css';

const LotteryForm = ({ 
  formData = {}, 
  onResult = () => {},
  isPreview = false 
}) => {
  const luckyRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  // 默认配置
  const defaultConfig = {
    title: '幸运大抽奖',
    subtitle: '转动转盘，赢取精美奖品',
    prizes: [
      { name: '一等奖', probability: 5, color: '#FF6B6B', icon: '🎁', coupon: 'PRIZE_001' },
      { name: '二等奖', probability: 15, color: '#4ECDC4', icon: '🎁', coupon: 'PRIZE_002' },
      { name: '三等奖', probability: 30, color: '#45B7D1', icon: '🎁', coupon: 'PRIZE_003' },
      { name: '谢谢参与', probability: 50, color: '#96CEB4', icon: '🍀', coupon: null }
    ],
    theme: {
      primaryColor: '#FF6B6B',
      secondaryColor: '#FFE66D',
      backgroundColor: '#FFF5F5',
      textColor: '#333333'
    },
    settings: {
      allowMultipleDraws: false,
      showProbability: false,
      autoGenerateCoupon: true
    }
  };

  // 合并配置
  const config = { ...defaultConfig, ...formData };
  const { title, subtitle, prizes, theme, settings } = config;

  // 生成转盘配置
  const wheelConfig = prizes.map((prize, index) => ({
    fonts: [{
      text: prize.name,
      top: '20%',
      fontSize: '16px',
      fontColor: '#333'
    }],
    background: prize.color,
    value: prize.name
  }));

  // 根据概率计算权重
  const calculateWeights = () => {
    return prizes.map(prize => prize.probability);
  };

  // 加权随机选择
  const weightedRandomSelect = () => {
    const weights = calculateWeights();
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let random = Math.random() * totalWeight;
    
    for (let i = 0; i < weights.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        return i;
      }
    }
    return weights.length - 1;
  };

  // 生成券码
  const generateCouponCode = (prize) => {
    if (!prize.coupon) return null;
    
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `${prize.coupon}_${timestamp}_${random}`.toUpperCase();
  };

  // 开始抽奖
  const startDraw = () => {
    if (isSpinning) return;
    if (hasDrawn && !settings.allowMultipleDraws) return;

    setIsSpinning(true);
    setResult(null);

    if (luckyRef.current) {
      luckyRef.current.play();
      
      // 延迟停止，确保转盘有足够时间旋转
      setTimeout(() => {
        const selectedIndex = weightedRandomSelect();
        const selectedPrize = prizes[selectedIndex];
        
        if (luckyRef.current) {
          luckyRef.current.stop(selectedIndex);
        }
        
        // 生成券码
        const coupon = selectedPrize.coupon ? generateCouponCode(selectedPrize) : null;
        setCouponCode(coupon);
        
        // 设置结果
        const drawResult = {
          prize: selectedPrize,
          couponCode: coupon,
          drawTime: new Date().toLocaleString(),
          isWinner: !!selectedPrize.coupon
        };
        
        setResult(drawResult);
        setHasDrawn(true);
        
        // 回调结果
        onResult(drawResult);
      }, 2000);
    }
  };

  // 重置抽奖
  const resetDraw = () => {
    setResult(null);
    setHasDrawn(false);
    setCouponCode('');
    setIsSpinning(false);
  };

  // 转盘结束回调
  const handleWheelEnd = (prize) => {
    setIsSpinning(false);
  };

  return (
    <div className={`lottery-form ${isPreview ? 'preview-mode' : ''}`} style={{ backgroundColor: theme.backgroundColor }}>
      {/* 标题区域 */}
      <div className="lottery-header">
        <h1 className="lottery-title" style={{ color: theme.primaryColor }}>
          {title}
        </h1>
        {subtitle && (
          <p className="lottery-subtitle" style={{ color: theme.textColor }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* 奖品展示 */}
      <div className="prizes-showcase">
        <h3>奖品设置</h3>
        <div className="prizes-grid">
          {prizes.map((prize, index) => (
            <div key={index} className="prize-item" style={{ backgroundColor: prize.color }}>
              <span className="prize-icon">{prize.icon}</span>
              <span className="prize-name">{prize.name}</span>
              {settings.showProbability && (
                <span className="prize-probability">{prize.probability}%</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 转盘区域 */}
      <div className="wheel-section">
        <div className="wheel-container">
          <LuckyWheel
            ref={luckyRef}
            width="300px"
            height="300px"
            prizes={wheelConfig}
            blocks={[{ padding: '10px', background: '#fff' }]}
            buttons={[{
              radius: '35%',
              background: theme.secondaryColor,
              pointer: true,
              fonts: [{
                text: isSpinning ? '转盘中...' : (hasDrawn && !settings.allowMultipleDraws ? '已抽奖' : '开始'),
                top: '-10px',
                fontColor: '#fff',
                fontSize: '16px'
              }]
            }]}
            onStart={startDraw}
            onEnd={handleWheelEnd}
          />
        </div>
      </div>

      {/* 抽奖结果 */}
      {result && (
        <div className="result-modal">
          <div className="result-content">
            <div className="result-icon">
              {result.isWinner ? '🎉' : '😊'}
            </div>
            <h3 className="result-title">
              {result.isWinner ? '恭喜中奖！' : '谢谢参与！'}
            </h3>
            <div className="result-prize">
              <span className="prize-icon">{result.prize.icon}</span>
              <span className="prize-name">{result.prize.name}</span>
            </div>
            
            {result.couponCode && (
              <div className="coupon-section">
                <h4>您的券码</h4>
                <div className="coupon-code">
                  {result.couponCode}
                </div>
                <p className="coupon-tip">
                  请保存此券码，凭券码领取奖品
                </p>
              </div>
            )}
            
            <div className="result-actions">
              {settings.allowMultipleDraws && (
                <button 
                  className="draw-again-btn"
                  onClick={resetDraw}
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  再抽一次
                </button>
              )}
              <button 
                className="close-result-btn"
                onClick={() => setResult(null)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 抽奖记录 */}
      {hasDrawn && (
        <div className="draw-record">
          <h4>抽奖记录</h4>
          <div className="record-item">
            <span className="record-time">{result?.drawTime}</span>
            <span className="record-prize">{result?.prize.name}</span>
            {result?.couponCode && (
              <span className="record-coupon">券码: {result.couponCode}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LotteryForm;
